import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Check, Loader2, AlertCircle, Apple } from 'lucide-react';
import axios from 'axios';
import FloatingDonateTab from '../components/FloatingDonateTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

const PopupShop = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState({});  // { item_id: quantity }
  const [name, setName] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);

  // Success / cancel states from Stripe redirect
  const orderStatus = params.get('status');
  const orderId = params.get('order_id');
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const fetchPopup = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/popup/${slug}`);
      setPopup(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "This pop-up shop isn't active right now." : 'Could not load this pop-up shop.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchPopup(); }, [fetchPopup]);

  useEffect(() => {
    if (orderStatus === 'success' && orderId) {
      axios.get(`${BACKEND_URL}/api/public/popup/${slug}/order/${orderId}`)
        .then(r => setConfirmedOrder(r.data)).catch(() => {});
    }
  }, [orderStatus, orderId, slug]);

  const updateQty = (itemId, delta) => {
    setCart((prev) => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev };
      if (next === 0) delete updated[itemId];
      else updated[itemId] = Math.min(next, 20);
      return updated;
    });
  };

  const cartLines = useMemo(() => {
    if (!popup) return [];
    return Object.entries(cart).map(([id, qty]) => {
      const item = popup.items.find((i) => i.id === id);
      if (!item) return null;
      return { ...item, quantity: qty, line_total: item.price * qty };
    }).filter(Boolean);
  }, [cart, popup]);

  const total = cartLines.reduce((s, l) => s + l.line_total, 0);
  const totalQty = cartLines.reduce((s, l) => s + l.quantity, 0);

  const handleCheckout = async () => {
    if (!name.trim()) { alert('Please enter your name.'); return; }
    if (cartLines.length === 0) { alert('Please add at least one item.'); return; }
    setCheckingOut(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/popup/${slug}/checkout`, {
        buyer_name: name.trim(),
        cart_items: cartLines.map((l) => ({ item_id: l.id, quantity: l.quantity })),
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  // ===== Loading / Error states =====
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#BA1826] animate-spin" />
      </div>
    );
  }

  if (error || !popup) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="popup-error">
        <div className="max-w-sm text-center">
          <AlertCircle className="w-10 h-10 text-[#BA1826] mx-auto mb-4" />
          <p className="font-heading text-xl mb-2">Pop-up not available</p>
          <p className="text-sm text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  // ===== Success page =====
  if (orderStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="popup-success">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-[#BA1826] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-3">Thank You!</h1>
          <p className="text-base text-white/85 leading-relaxed mb-6">
            Thank you for supporting Novena Humanitarian Foundation.
          </p>
          <div className="bg-white text-[#0A0A0A] rounded-lg p-5 mb-6 text-left">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-bold mb-2">Show this to our team</p>
            {confirmedOrder ? (
              <>
                <p className="font-mono text-base font-bold mb-1" data-testid="popup-success-order-number">{confirmedOrder.order_number}</p>
                <p className="text-xs text-[#4B5563] mb-3">{confirmedOrder.buyer_name}</p>
                <div className="space-y-1 text-xs border-t border-[#E5E7EB] pt-2">
                  {(confirmedOrder.line_items || []).map((li, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{li.item_name} × {li.quantity}</span>
                      <span className="font-mono">{formatPrice(li.line_total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold pt-2 border-t border-[#E5E7EB] mt-2">
                    <span>Total</span>
                    <span className="font-mono">{formatPrice(confirmedOrder.total)}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-[#4B5563]">Order ID: <span className="font-mono">{orderId}</span></p>
            )}
          </div>
          <a href={`/popup/${slug}`} className="text-xs text-white/60 hover:text-white underline">← Back to pop-up</a>
        </div>
      </div>
    );
  }

  // ===== Cancelled state (light note) =====
  const cancelledNote = orderStatus === 'cancelled';

  // ===== Main Storefront =====
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-32" data-testid="popup-page">
      {/* Header */}
      <header className="bg-[#BA1826] py-6 px-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-1">Novena Humanitarian Foundation</p>
        <h1 className="font-heading text-2xl font-bold" data-testid="popup-title">{popup.name}</h1>
        <p className="text-xs text-white/80 mt-1">{popup.subtitle}</p>
      </header>

      {cancelledNote && (
        <div className="bg-amber-500/10 border-y border-amber-500/30 text-amber-300 text-xs text-center py-2 px-4">
          Checkout cancelled — your cart is still here. Try again when you're ready.
        </div>
      )}

      {/* Items */}
      <main className="max-w-md mx-auto px-4 py-6 space-y-3">
        {popup.items.length === 0 && (
          <p className="text-center text-white/60 text-sm py-8">No items available yet.</p>
        )}
        {popup.items.map((item) => {
          const qty = cart[item.id] || 0;
          const outOfStock = item.remaining === 0;
          return (
            <div key={item.id} className={`bg-white text-[#0A0A0A] rounded-lg overflow-hidden ${outOfStock ? 'opacity-50' : ''}`} data-testid={`popup-item-${item.id}`}>
              <div className="flex">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover flex-shrink-0" />
                )}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div>
                    <p className="font-heading text-sm font-bold truncate">{item.name}</p>
                    {item.description && (
                      <p className="text-[10px] text-[#4B5563] line-clamp-2 mt-0.5">{item.description}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-base text-[#BA1826]">{formatPrice(item.price)}</span>
                    {outOfStock ? (
                      <span className="text-[10px] font-bold uppercase text-[#9CA3AF]">Sold out</span>
                    ) : qty === 0 ? (
                      <button
                        onClick={() => updateQty(item.id, 1)}
                        className="bg-[#0A0A0A] text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded"
                        data-testid={`popup-add-${item.id}`}
                      >
                        Add
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 bg-[#F1F5F9] rounded flex items-center justify-center" data-testid={`popup-minus-${item.id}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-bold text-sm w-6 text-center" data-testid={`popup-qty-${item.id}`}>{qty}</span>
                        <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 bg-[#0A0A0A] text-white rounded flex items-center justify-center" data-testid={`popup-plus-${item.id}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </main>

      {/* Sticky Checkout Bar */}
      {cartLines.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 p-4 z-40" data-testid="popup-checkout-bar">
          <div className="max-w-md mx-auto space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{totalQty} item{totalQty !== 1 ? 's' : ''}</span>
              <span className="font-mono font-bold text-lg" data-testid="popup-total">{formatPrice(total)}</span>
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={80}
              className="w-full px-3 py-2.5 text-sm bg-white text-[#0A0A0A] rounded focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
              data-testid="popup-name-input"
            />
            <button
              onClick={handleCheckout}
              disabled={checkingOut || !name.trim()}
              className="w-full bg-[#BA1826] text-white font-bold uppercase tracking-wide text-sm py-3 rounded disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="popup-checkout-btn"
            >
              {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              {checkingOut ? 'Opening checkout…' : `Pay ${formatPrice(total)}`}
            </button>
            <p className="text-[9px] text-white/40 text-center flex items-center justify-center gap-1.5">
              <Apple className="w-3 h-3" /> Apple Pay · Google Pay · Card · Secured by Stripe
            </p>
          </div>
        </div>
      )}

      {/* Persistent Donate Now CTA — lifts above checkout bar when cart is active */}
      <FloatingDonateTab lifted={cartLines.length > 0} />
    </div>
  );
};

export default PopupShop;
